import React, { useState } from "react";
import { Card, Typography, Box, Icon } from "@mui/material";
import { ErrorOutlineOutlined, MoreVert } from "@mui/icons-material";

import { EditDeleteMenu, IconButton } from "../v3";

/**
 * A card component that displays a list of cards with optional actions.
 * @param {Object} data The data to display in the card.
 * @param {ReactNode} icon The icon to display before the title.
 * @param {string} name The title of the card.
 * @param {boolean} showAlert Whether to show an exclamation icon after the title.
 * @param {string} description The subtitle of the card.
 * @param {boolean} hasViewButton Whether to show a view button.
 * @param {function} onViewClick Called when the view button is clicked.
 * @param {boolean} hasEditButton Whether to show an edit button.
 * @param {boolean} hasDeleteButton Whether to show a delete button.
 * @param {function} onEdit Called when the edit button is clicked.
 * @param {function} onDelete Called when the delete button is clicked.
 */
export const ActionCard = ({
  data = {},
  icon = null,
  name = "",
  showAlert = false,
  description = null,
  hasViewButton = false,
  onViewClick = () => {},
  hasEditButton = false,
  hasDeleteButton = false,
  onEdit = () => {},
  onDelete = () => {},
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleEdit = () => {
    handleMenuClose();
    onEdit(data);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete(data);
  };

  return (
    <Card
      sx={{
        flex: 1,
        minWidth: 250,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingLeft: 4,
        paddingRight: hasEditButton || hasDeleteButton ? 2 : 4,
        paddingY: 2,
        boxShadow: 3,
        borderRadius: 3,
      }}
    >
      {/* icon + title + subtitle */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: description && description.trim() !== "" ? "flex-start" : "center",
          minHeight: 48,
          maxWidth: 200,
          flexShrink: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden" }}>
          {icon && <Icon sx={{ fontSize: 22, color: "#674BF5", opacity: 0.9 }}>{icon}</Icon>}

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: "#674BF5",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              flexShrink: 1,
            }}
          >
            {name}
          </Typography>

          {showAlert && <ErrorOutlineOutlined sx={{ color: "#757575", fontSize: 18 }} />}
        </Box>

        {description && description.trim() !== "" ? (
          <Typography
            variant="caption"
            sx={{
              display: "block",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            {description}
          </Typography>
        ) : (
          <Box sx={{ height: 0, marginTop: 0 }} />
        )}
      </Box>

      {/* view button + 3-dot menu */}
      {(hasViewButton || hasEditButton || hasDeleteButton) && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {hasViewButton && (
            <IconButton
              buttonText="view"
              onClick={() => onViewClick(data)}
              styles={{
                color: "#757575",
                backgroundColor: "#F4F4F4",
                padding: "3px 8px",
                fontSize: "0.8rem",
              }}
              hoverStyles={{ backgroundColor: "#EBE9F9" }}
            />
          )}

          {(hasEditButton || hasDeleteButton) && (
            <IconButton
              icon={<MoreVert />}
              tooltip="options"
              onClick={handleMenuClick}
              styles={{
                backgroundColor: "transparent",
                borderRadius: 0,
                color: "#757575",
                padding: 0,
              }}
              hoverStyles={{
                backgroundColor: "transparent",
                color: "#674BF5",
              }}
            />
          )}
        </Box>
      )}
      {/* edit delete menu */}
      <EditDeleteMenu
        anchorEl={anchorEl}
        open={openMenu}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onClose={handleMenuClose}
        canEdit={hasEditButton}
        canDelete={hasDeleteButton}
      />
    </Card>
  );
};
